/**
 * Floating "Ask AI Tutor" button + slide-up chat panel.
 *
 * Drop into any FocusMode page via the `tutorSlot` prop. The component is
 * fully self-contained: it manages its own conversation state and posts to
 * an existing AI endpoint. Falls back to a friendly empty state if the
 * server returns an error so a glitch never blocks the learner.
 *
 * Wire to whichever backend endpoint is appropriate for the surface:
 *   - Challenger Series:  /api/challenger/ai-tutor
 *   - Resources:          /api/resources/ai-tutor   (or stem-library/generate)
 *   - Practice / Mock:    /api/ai-tutor/chat
 */

import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const QUICK_ACTIONS = [
  { label: 'Explain this', prompt: 'Can you explain this topic in simple terms?' },
  { label: 'Give an example', prompt: 'Show me a worked example.' },
  { label: 'Try a similar question', prompt: 'Give me a similar practice question.' },
  { label: 'Make it simpler', prompt: 'Simplify the concept for me.' },
];

const AskAITutorButton = ({ endpoint = '/api/ai-tutor/chat', context = {} }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'tutor', text}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const sendMessage = async (text) => {
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${BACKEND_URL}${endpoint}`,
        { message: text, context },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      const reply = resp.data?.reply || resp.data?.message || resp.data?.content || 'I had trouble forming a response — try rephrasing your question.';
      setMessages((m) => [...m, { role: 'tutor', text: reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'tutor',
          text:
            "I'm having trouble connecting to the AI tutor right now. Please try again in a moment, or ask your teacher.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating action button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-4 md:bottom-24 md:right-6 z-50 px-4 py-3 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold shadow-2xl shadow-purple-500/40 flex items-center gap-2 hover:shadow-purple-500/60 transition-shadow"
        data-testid="ask-ai-tutor-btn"
        aria-label={open ? 'Close AI Tutor' : 'Open AI Tutor'}
        aria-expanded={open}
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        <span className="hidden sm:inline">{open ? 'Close' : 'Ask AI Tutor'}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className="fixed bottom-40 right-4 md:bottom-40 md:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] bg-white text-slate-900 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
            data-testid="ai-tutor-panel"
            role="dialog"
            aria-label="AI Tutor chat"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-3 flex items-center gap-2">
              <div className="bg-white/20 p-1.5 rounded-lg">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Edpies AI Tutor</p>
                <p className="text-xs text-white/80 truncate">
                  {context.topic ? `Talking about: ${context.topic}` : 'How can I help?'}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-md hover:bg-white/15"
                aria-label="Close AI Tutor"
                data-testid="ai-tutor-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50 text-sm">
              {messages.length === 0 && (
                <>
                  <div className="text-slate-500 text-sm">
                    Hi! I can explain the current topic, give examples, generate similar questions, or simplify hard concepts.
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {QUICK_ACTIONS.map((a) => (
                      <button
                        key={a.label}
                        onClick={() => sendMessage(a.prompt)}
                        className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200"
                        data-testid={`ai-tutor-quick-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-purple-600 text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking…
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage(input.trim());
              }}
              className="border-t border-slate-200 p-3 flex gap-2 bg-white"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything about this topic…"
                disabled={loading}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                data-testid="ai-tutor-input"
                aria-label="Type your question"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="px-3 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-40 hover:bg-purple-700"
                data-testid="ai-tutor-send-btn"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskAITutorButton;
